import React, { useState, useRef, useEffect, Ref } from 'react';
import { useReactToPrint } from 'react-to-print';
import { httpGet } from '../../../modules/Backend';
import { getCurrentFormattedDate } from '../../../helpers/DateHelper';
import LoadingButton from '../LoadingButton';
import { EntityNames } from '../../../enums/EntityNames';

interface PrintableComponentProps {
  data: any;
  disposalData: any,
  boxPages: Box[],
  cubePages: Cube[],
  ref: React.RefObject<HTMLDivElement>
}

interface Cube extends ISortable {
  reference: string;
  breadcrumb: string;
  outerCubeType: string;
  couldWholeOuterCubeBeDisposed: string;
  packingBoxCount: number
  sortOrder: number;
}

interface Box extends ISortable {
  reference: string;
  volume: number;
  sequenceNumber: number;
  breadcrumb: string;
  sortOrder: number;
}

interface ISortable {
  sortOrder: number;
}

function splitIntoColumns<T extends ISortable>(items: T[], columnCount: number): T[][] {
  // Sort first by sortOrder
  const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);

  // Determine how large each column should be
  const baseSize = Math.floor(sorted.length / columnCount);
  const remainder = sorted.length % columnCount;

  const columns: T[][] = [];

  let start = 0;
  for (let i = 0; i < columnCount; i++) {
    // If we have a remainder, this column gets 1 more
    const size = baseSize + (i < remainder ? 1 : 0);
    columns.push(sorted.slice(start, start + size));

    start += size;
  }
  return columns;
}

// Printable component with a ref to allow printing
const PrintableComponent = React.forwardRef<HTMLDivElement, PrintableComponentProps>(({ data, disposalData, cubePages, boxPages }, ref) => (
  <div ref={ref}>
    {data && (
      <div className='disposal-print-view__wrapper'>
        <div className='disposal-print-view__header'>
          <p>{getCurrentFormattedDate()}</p>
        </div>
        <div className='disposal-print-view__information'>
          <h1>Disposal {disposalData.reference}</h1>
          <div>
            <h2>Account:</h2>
            <p className='margin__none'>{disposalData.accountName}</p>
          </div>
          <div>
            <h2>Requested By:</h2>
            <p className='margin__none'>{disposalData.requestedBy}</p>
          </div>
        </div>
        <div className='disposal-print-view__section'>
          <h2>Pallet Overview</h2>
          <div className='disposal-print-view__table-wrapper'>
            {splitIntoColumns(cubePages, 2).map((column, colIdx) => (
              <table key={colIdx} className='disposal-print-view__table'>
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Pallet</th>
                    <th>Type</th>
                    <th>Whole Pallet?</th>
                    <th>Packing Box Count</th>
                  </tr>
                </thead>
                <tbody>
                  {column?.map((item: Cube, idx: number) => (
                    <tr key={idx}>
                      <td>{item.breadcrumb}</td>
                      <td>{item.reference}</td>
                      <td>{item.outerCubeType}</td>
                      <td>{item.couldWholeOuterCubeBeDisposed}</td>
                      <td>{item.packingBoxCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </div>
        </div>
        <div className='disposal-print-view__page-break' />
        <div className='disposal-print-view__section'>
          <h2>Box Overview</h2>
          <div className='disposal-print-view__table-wrapper'>
            {splitIntoColumns(boxPages, 2).map((column, colIdx) => (
              <table key={colIdx} className='disposal-print-view__table'>
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Box</th>
                    <th>Volume</th>
                    <th>Sequence</th>
                  </tr>
                </thead>
                <tbody>
                  {column?.map((item: Box, idx: number) => (
                    <tr key={idx}>
                      <td>{item.breadcrumb}</td>
                      <td>{item.reference}</td>
                      <td>{item.volume}</td>
                      <td>{item.sequenceNumber}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </div>
        </div>
      </div>
    )}
  </div>
)
);

interface PrinterProps {
  disposalId: string;
  disposalData: any,
  save: Function;
}

function PrintDisposalPickList({ disposalId, disposalData, save }: PrinterProps) {
  const [data, setData] = useState<any>();
  const [shouldPrint, setShouldPrint] = useState(false);
  const [loading, setLoading] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  const fetchData = async () => {
    const printDataResponse = await httpGet(`api/${EntityNames.disposals}/pickPrintData?disposalId=${disposalId}`);
    if (printDataResponse.data) {
      setData(printDataResponse.data);
      setShouldPrint(true);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => { setShouldPrint(false); setLoading(false) }
  });

  useEffect(() => {
    if (shouldPrint && componentRef.current) {
      handlePrint();
    }
  }, [shouldPrint]);

  const handleFetchAndPrint = async () => {
    if (loading)
      return
    setLoading(true)
    await save();
    await fetchData();
  };

  const cubes = data?.outerCubes || [];
  const boxes = data?.items || [];

  return (
    <div>
      <LoadingButton className="button button--primary button--with-icon button--small"
        onPress={handleFetchAndPrint}
        isDisabled={loading}
        isLoading={loading}
        loadingText="Preparing"
        buttonText="Print Pick List">
        <></>
      </LoadingButton>
      <div style={{ display: 'none' }}>
        <PrintableComponent ref={componentRef} data={data} disposalData={disposalData} cubePages={cubes} boxPages={boxes} />
      </div>
    </div>
  );
};

export default PrintDisposalPickList;